// src/pages/Order/Purchase Order/Purchase Order Entry/PurchaseOrderEntry.jsx
import React, { useEffect, useState } from "react";
import { Row, Col, Button, Flex, Grid } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CreatePurchaseOrderForm from "./components/CreatePurchaseOrderForm";
import PurchaseOrderTable from "./components/PurchaseOrderTable";
import { createPurchaseOrder, fetchPurchaseOrderNumber } from "./purchaseOrderThunk";
import { resetPurchaseOrder, setFormField } from "./purchaseOrderSlice";
import Swal from "sweetalert2";
import { getAccessType } from "../../../../lib/functions";
import { fetchSingleInstitutionDetails } from "../../../../store/commonThunks/lookups";
import { selectInstitutionData } from "../../../../store/commonSelectors/lookupsSelectors";

const PurchaseOrderEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();

  let [access_type, access_type_set] = useState("");
  const { id } = useParams();
  const institution = useSelector(selectInstitutionData);

  useEffect(() => {
    const access = getAccessType("purchase_order_entry");
    access_type_set(access);
    dispatch(fetchSingleInstitutionDetails());
  }, []);

  // Bring purchaseOrder state
  const {
    itemCart,
    supplierInfo,
    footerValues,
    voucherNumber,
    entryDate,
    dueDate,
    tagFieldId,
  } = useSelector((s) => s.purchaseOrder);

  // empty row sanitization
  function sanitizePurchaseOrderItems(items) {
    return items.filter(row =>
      row.item_id &&
      Number(row.quantity) > 0
    );
  }
  // duplicate row validation 
  function validateDuplicatePurchaseOrderLines(items) {
    const seen = new Set();

    for (const row of items) {
      const key = [
        row.item_id,
        Number(row.rate || 0).toFixed(2),
        Boolean(row.bonus)
      ].join("|");

      if (seen.has(key)) {
        return {
          valid: false,
          message: `Duplicate item detected: ${row.product}`
        };
      }

      seen.add(key);
    }

    return { valid: true };
  }


  // Save handler
  const handleSave = () => {
    if (!supplierInfo || !supplierInfo.acc_id) {
      Swal.fire("Select supplier!", "", "warning");
      return;
    }

    const cleanedItems = sanitizePurchaseOrderItems(itemCart);

    if (!itemCart || cleanedItems.length === 0) {
      Swal.fire(
        "Invalid Purchase Order",
        "Please add at least one item with quantity.",
        "warning"
      );
      return;
    }

    const { valid, message } =
      validateDuplicatePurchaseOrderLines(cleanedItems);

    if (!valid) {
      Swal.fire("Duplicate Item", message, "warning");
      return;
    }

    if (footerValues?.description && footerValues.description.length > 255) {
      Swal.fire("Description max size exceeded", "", "warning");
      return;
    }

    dispatch(
      setFormField({
        field: "itemCart",
        value: cleanedItems,
      })
    );

    Swal.fire({
      title: "Saving Purchase Order...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    const isEdit = !!id;
    dispatch(createPurchaseOrder({ items: cleanedItems }))
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {
          dispatch(resetPurchaseOrder());
          dispatch(fetchPurchaseOrderNumber());
          Swal.fire({
            title: isEdit ? `Purchase Order Updated!. Do you want to view invoice?` :
              `Purchase Order Created!. Do you want to view invoice?`,
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Yes, view invoice",
            cancelButtonText: "No, go back",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/order/quick-purchase-order-voucher/${res.pur_o_id}`);
            } else {
              navigate("/order/purchase");
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };

  // Back button
  const handleBack = () => {
    // if (location.state?.from) {
    //   navigate(location.state.from);
    // } else {
    navigate("/order/dashboard");
    // }
  };

  // Reset button
  const handleReset = () => {
    dispatch(resetPurchaseOrder());
    Swal.fire("Form Reset", "All fields and items cleared", "info");
    dispatch(fetchPurchaseOrderNumber());
  };

  const ActionButtons = () => (
    <Flex justify="flex-end" gap={16} style={{ marginBottom: 16 }}>
      <Button onClick={handleBack}>Back</Button>
      <Button onClick={handleReset}>Reset</Button>
      {access_type == "WRITE" && <>
        <Button type="primary" onClick={handleSave} data-testid="final-save-btn">
          Save
        </Button></>}


    </Flex>
  );

  return (
    <div style={{ padding: screens.xs ? 12 : 20, overflowX: "hidden", width: "100%" }}>
      {/* Top Buttons */}
      <Row>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>

      {/* Form & Table */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <CreatePurchaseOrderForm
            editId={id}
            institution={institution}
          />
        </Col>

        <Col span={24}>
          <PurchaseOrderTable editId={id} institution={institution}/>
        </Col>
      </Row>

      {/* Bottom Buttons */}
      <Row style={{ marginTop: 24 }}>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>
    </div>
  );
};

export default PurchaseOrderEntry;
